/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Rectangle, Texture } from 'pixi.js-legacy';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { LightboxMask } from '@appCanvasCommon/export/LightboxMask';
/**
 * The Lightbox is a thin wrapper around a FancySprite that is used to create a greyed out visual with a punched out
 * "see through" window in the texture. By default it does not have interaction, however you can use this object as
 * an interaction blocker by drawing this above everything in screen space and then adding interaction to it.
 */
export class Lightbox extends FancySprite {
	/**
	 * Public API for accessing the world viewport
	 */
	get worldView(): Rectangle {
		return this._worldView;
	}
	/**
	 * Storing the world rectangle of the Viewport on the lightbox so it can be more readily consumed by custom drawers
	 * @private
	 */
	private _worldView: Rectangle = new Rectangle(0, 0, 1, 1);
	/**
	 * Constructor for the lightbox. Makes it exist or something
	 * @param texture The PIXI.Texture to use to represent a lightbox. By default we are using a 1x1 white square because
	 * we can set the color of this square to anything we want via the tint property.
	 */
	constructor(texture: Texture = Texture.WHITE) {
		super(texture);
	}

	/**
	 * Helper method to set a mask to the lightbox. The object passed in will represent the "see through" area that is
	 * visible through the Lightbox texture.
	 * @param mask
	 */
	public setMask(mask: LightboxMask): void {
		this.mask = mask;
	}
	/**
	 * Destroys the display item and cleans up all resources.
	 * This method destroys all child objects and removes references
	 * @param options Destruction options that control how child objects are destroyed
	 */
	public override destroy(options: DestroyOptions = DestroyOptions.sourceOnly): void {
		super.destroy(options);
	}

	/**
	 * Helper method to set the size of the lightbox
	 * @param width Width of the lightbox
	 * @param height Height of the lightbox
	 */
	public setSize(width: number, height: number): void {
		this.width = width;
		this.height = height;
	}

	public setWorldSize(worldRect: Rectangle): void {
		this._worldView = worldRect;
	}
}
